import type { Resolvers } from "./types"
import { defaultPipeRegistry, type PipeRegistry } from "./pipe-registry"

/**
 * Evaluate a string containing one or more `#{...}` expressions.
 *
 * - If the entire (trimmed) string is a single `#{...}` expression, the raw resolver value is
 *   returned (preserves objects/numbers for downstream JSON serialization).
 * - Otherwise each `#{...}` span is substituted with `String(value)` inside the surrounding text.
 */
export function evaluateExpression(
  str: string,
  resolvers: Resolvers,
  warnings: string[],
): unknown {
  const regex = /#\{([^}]+)\}/g
  const matches = [...str.matchAll(regex)]

  if (matches.length === 1 && matches[0][0] === str.trim()) {
    return evalOne(matches[0][1].trim(), resolvers, warnings)
  }

  return str.replace(regex, (_, expr: string) => {
    const v = evalOne(expr.trim(), resolvers, warnings)
    return v === undefined || v === null ? "" : String(v)
  })
}

/**
 * Evaluate one expression body (without the surrounding `#{...}`).
 *
 * Operator precedence (lowest to highest — `?:` binds loosest, `??` and `|` still work last):
 *   1. Ternary `cond ? then : else`
 *   2. Logical OR `||`
 *   3. Logical AND `&&`
 *   4. Equality `===` `!==` `==` `!=`
 *   5. Relational `<` `<=` `>` `>=`
 *   6. Nullish coalescing `??`  (existing PoC semantics; also falls back on empty string)
 *   7. Pipe `|` transforms
 *   8. Primary: namespace dispatch / literals
 *
 * Backward compat: any expression that does NOT contain ternary / boolean / comparison operators
 * follows the exact pre-PR #26 code path (??, |, namespace). Existing tests covering
 * `#{__bind.X | format:currency}` and `#{__i18n.X ?? 'fallback'}` are unaffected.
 */
function evalOne(expr: string, resolvers: Resolvers, warnings: string[]): unknown {
  const trimmed = expr.trim()
  if (trimmed === "") {
    warnings.push("empty expression")
    return undefined
  }

  // --- Precedence 1: ternary ---------------------------------------------------------------
  const ternary = splitTernary(trimmed)
  if (ternary) {
    const cond = evalOne(ternary.cond, resolvers, warnings)
    return truthy(cond)
      ? evalOne(ternary.then, resolvers, warnings)
      : evalOne(ternary.else, resolvers, warnings)
  }

  // --- Precedence 2: logical || -----------------------------------------------------------
  // Split on the FIRST top-level `||`. Note `??` also contains no `|` adjacency issue since we
  // look for the exact `||` token; the pipe operator `|` never appears doubled.
  const orIdx = findTopLevelOp(trimmed, "||")
  if (orIdx !== -1) {
    const left = trimmed.slice(0, orIdx).trim()
    const right = trimmed.slice(orIdx + 2).trim()
    const lv = evalOne(left, resolvers, warnings)
    if (truthy(lv)) return lv
    return evalOne(right, resolvers, warnings)
  }

  // --- Precedence 3: logical && -----------------------------------------------------------
  const andIdx = findTopLevelOp(trimmed, "&&")
  if (andIdx !== -1) {
    const left = trimmed.slice(0, andIdx).trim()
    const right = trimmed.slice(andIdx + 2).trim()
    const lv = evalOne(left, resolvers, warnings)
    if (!truthy(lv)) return lv
    return evalOne(right, resolvers, warnings)
  }

  // --- Precedence 4: equality ------------------------------------------------------------
  // Check longer tokens first so `===` isn't caught by `==`.
  for (const op of ["===", "!==", "==", "!="] as const) {
    const idx = findTopLevelOp(trimmed, op)
    if (idx !== -1) {
      const left = trimmed.slice(0, idx).trim()
      const right = trimmed.slice(idx + op.length).trim()
      const lv = evalOne(left, resolvers, warnings)
      const rv = evalOne(right, resolvers, warnings)
      switch (op) {
        case "===":
          return lv === rv
        case "!==":
          return lv !== rv
        case "==":
          // eslint-disable-next-line eqeqeq
          return lv == rv
        case "!=":
          // eslint-disable-next-line eqeqeq
          return lv != rv
      }
    }
  }

  // --- Precedence 5: relational -----------------------------------------------------------
  for (const op of ["<=", ">=", "<", ">"] as const) {
    const idx = findTopLevelOp(trimmed, op)
    if (idx !== -1) {
      const left = trimmed.slice(0, idx).trim()
      const right = trimmed.slice(idx + op.length).trim()
      const lv = evalOne(left, resolvers, warnings)
      const rv = evalOne(right, resolvers, warnings)
      const ln = toNumber(lv)
      const rn = toNumber(rv)
      switch (op) {
        case "<=":
          return ln <= rn
        case ">=":
          return ln >= rn
        case "<":
          return ln < rn
        case ">":
          return ln > rn
      }
    }
  }

  // --- Precedence 6: nullish coalescing ?? ------------------------------------------------
  // (preserved from PoC — also treats empty string as nullish per original PoC semantics.)
  const nullishIdx = findTopLevelOp(trimmed, "??")
  if (nullishIdx !== -1) {
    const left = trimmed.slice(0, nullishIdx).trim()
    const right = trimmed.slice(nullishIdx + 2).trim()
    const leftVal = evalOne(left, resolvers, warnings)
    if (leftVal !== undefined && leftVal !== null && leftVal !== "") return leftVal
    // Right side: literal single-quoted string or another expression. Also support `null`.
    if (right === "null") return null
    if (right.startsWith("'") && right.endsWith("'")) {
      return right.slice(1, -1)
    }
    if (right.startsWith('"') && right.endsWith('"')) {
      return right.slice(1, -1)
    }
    return evalOne(right, resolvers, warnings)
  }

  // --- Precedence 7: pipe | ---------------------------------------------------------------
  const pipeIdx = findTopLevelOp(trimmed, "|")
  if (pipeIdx !== -1) {
    const valueExpr = trimmed.slice(0, pipeIdx).trim()
    const pipeExpr = trimmed.slice(pipeIdx + 1).trim()
    const v = evalOne(valueExpr, resolvers, warnings)
    // If the value is an __app pass-through (string matching `#{__app...}`), preserve the pipe in
    // the output — the runtime evaluator applies the pipe at render time. Matches pilot §6 output
    // where `#{__app.products.items[iterateIndex].price | format:currency}` stays intact.
    if (typeof v === "string" && /^#\{__app\./.test(v) && v.endsWith("}")) {
      const inner = v.slice(2, -1) // drop surrounding "#{" and "}"
      return `#{${inner} | ${pipeExpr}}`
    }
    return applyPipe(v, pipeExpr, resolvers.__pipe ?? defaultPipeRegistry(), warnings)
  }

  // --- Precedence 8: primary --------------------------------------------------------------
  return evalPrimary(trimmed, resolvers, warnings)
}

/**
 * Evaluate a primary (atomic) expression: literal or namespace dispatch.
 */
function evalPrimary(expr: string, resolvers: Resolvers, warnings: string[]): unknown {
  // Parenthesized sub-expression.
  if (expr.startsWith("(") && expr.endsWith(")")) {
    // Ensure the outer parens match (aren't two separate groups like `(a)+(b)`).
    if (matchingParen(expr, 0) === expr.length - 1) {
      return evalOne(expr.slice(1, -1).trim(), resolvers, warnings)
    }
  }

  // Literals.
  if (expr === "null") return null
  if (expr === "undefined") return undefined
  if (expr === "true") return true
  if (expr === "false") return false
  if (/^-?\d+(?:\.\d+)?$/.test(expr)) return Number(expr)
  if (expr.startsWith("'") && expr.endsWith("'") && expr.length >= 2) return expr.slice(1, -1)
  if (expr.startsWith('"') && expr.endsWith('"') && expr.length >= 2) return expr.slice(1, -1)

  // Unary NOT.
  if (expr.startsWith("!")) {
    return !truthy(evalOne(expr.slice(1).trim(), resolvers, warnings))
  }

  // Namespace dispatch. Bracket segments containing dynamic expressions (e.g.
  // `pricing[__bind.tier.id].name`) are materialized first via `expandNamespacePath` so the
  // resolver sees a plain `.pricing.basic.name`-style path. Unresolvable bracket bodies return
  // `undefined` from the outer call; the caller can surface that via `?? fallback`.
  // NOTE (silent-blank on missing slot — KNOWN ISSUE, fix scoped to the Variant rung): a referenced
  // __bind/__i18n/__symbol path the caller does not provide resolves to `undefined` and the field
  // blanks on serialization. A blanket "warn on undefined" is a FALSE POSITIVE for legitimately
  // OPTIONAL slots (e.g. a `visibleIf`-guarded badge whose value is intentionally null), which the
  // binder cannot distinguish from a missing REQUIRED slot without a typed/required bindingSchema.
  // The correct fix is a bindingSchema.required-aware post-bind check at the generation layer — see
  // docs/agentic/ONTOLOGY-GENERATION-VARIANT-RUNG-DESIGN.
  if (expr.startsWith("__bind.")) {
    const path = expandNamespacePath(expr.slice("__bind.".length), resolvers, warnings)
    if (path === undefined) return undefined
    return resolvers.__bind.resolve(path)
  }
  if (expr.startsWith("__i18n.")) {
    const path = expandNamespacePath(expr.slice("__i18n.".length), resolvers, warnings)
    if (path === undefined) return undefined
    return resolvers.__i18n.resolve(path)
  }
  if (expr.startsWith("__symbol.")) {
    const path = expandNamespacePath(expr.slice("__symbol.".length), resolvers, warnings)
    if (path === undefined) return undefined
    return resolvers.__symbol.resolve(path)
  }
  if (expr.startsWith("__ds.")) {
    const path = expandNamespacePath(expr.slice("__ds.".length), resolvers, warnings)
    if (path === undefined) return undefined
    return resolvers.__ds.resolveTokenPath(path)
  }
  if (expr.startsWith("__app.")) {
    // Expand bracket bodies at bind time but keep the `__app.*` prefix as a runtime
    // pass-through. The runtime renderer resolves `__app.*` against live store/app data,
    // but only once brackets have been materialized into literal segments here.
    const path = expandNamespacePath(expr.slice("__app.".length), resolvers, warnings)
    if (path === undefined) return undefined
    return `#{__app.${path}}`
  }
  // Iteration-frame references — injected by the `iterateOver` expansion pass on bindExample's
  // walk (see bind-example.ts → expandIteration). `__item` is the current array element,
  // `__index` the 0-based position. Nested iteration places the enclosing frame at
  // `r.__iter.parent` (not read here — inner frames always see the most recent item, matching
  // lexical-scope shadowing in the pilot spec).
  if (expr === "__index") {
    if (!resolvers.__iter) {
      // Inside a preserved-iterateOver subtree: pass through verbatim so the runtime renderer
      // resolves `__index` when it actually expands the iteration.
      if (resolvers.__iterDeferred) return `#{__index}`
      warnings.push(`__index used outside iteration frame`)
      return undefined
    }
    return resolvers.__iter.index
  }
  if (expr === "__item" || expr.startsWith("__item.") || expr.startsWith("__item[")) {
    if (!resolvers.__iter) {
      if (resolvers.__iterDeferred) return `#{${expr}}`
      warnings.push(`__item used outside iteration frame`)
      return undefined
    }
    if (expr === "__item") return resolvers.__iter.item
    // `__item.path` → walk the object path on the current item. Bracket indexing supports both
    // literals (`__item[0]`, `__item["name"]`) and dynamic expressions (`__item[__bind.field]`,
    // `__item.orders[__index].total`) — dynamic bodies are evaluated recursively through
    // `evalOne` before path-walking.
    return resolveItemPath(resolvers.__iter.item, expr.slice("__item".length), resolvers, warnings)
  }

  warnings.push(`unknown namespace in expression: ${expr}`)
  return undefined
}

/**
 * Resolve a `.a.b[0].c`-style path against the current iteration item. Keys that don't exist
 * return `undefined` (no warning — mirrors BindResolver behavior for unresolved paths).
 *
 * Bracket bodies support literals (integer, quoted string) AND dynamic expressions
 * (e.g. `[__bind.activeField]`, `[__index]`) — dynamic bodies are evaluated via the full
 * expression evaluator, so nesting and ternaries Just Work.
 */
function resolveItemPath(
  item: unknown,
  suffix: string,
  resolvers: Resolvers,
  warnings: string[],
): unknown {
  const segments: Array<string | number> = []
  let i = 0
  while (i < suffix.length) {
    const ch = suffix[i]
    if (ch === ".") {
      let j = i + 1
      while (j < suffix.length && suffix[j] !== "." && suffix[j] !== "[") j++
      segments.push(suffix.slice(i + 1, j))
      i = j
      continue
    }
    if (ch === "[") {
      const close = suffix.indexOf("]", i)
      if (close === -1) {
        warnings.push(`unterminated bracket in __item path: ${suffix}`)
        return undefined
      }
      const body = suffix.slice(i + 1, close).trim()
      if (/^\d+$/.test(body)) {
        segments.push(Number(body))
      } else if (
        (body.startsWith('"') && body.endsWith('"')) ||
        (body.startsWith("'") && body.endsWith("'"))
      ) {
        segments.push(body.slice(1, -1))
      } else {
        // Dynamic bracket — evaluate via the full expression evaluator so nested bind /
        // ternaries / pipes all work. Numeric results stay as array indices; everything else
        // coerces to string. Unresolved (undefined/null) body short-circuits the whole path.
        const value = evalOne(body, resolvers, warnings)
        if (value === undefined || value === null) return undefined
        segments.push(typeof value === "number" ? value : String(value))
      }
      i = close + 1
      continue
    }
    warnings.push(`unexpected char in __item path: ${ch}`)
    return undefined
  }

  let cur: unknown = item
  for (const seg of segments) {
    if (cur === undefined || cur === null) return undefined
    if (typeof seg === "number") {
      if (!Array.isArray(cur)) return undefined
      cur = cur[seg]
    } else {
      if (typeof cur !== "object") return undefined
      cur = (cur as Record<string, unknown>)[seg]
    }
  }
  return cur
}

/**
 * Expand dynamic bracket expressions inside a namespace path. `[body]` with a literal body
 * (integer or quoted string) passes through unchanged; `[body]` with any other content is
 * evaluated via `evalOne` and rewritten as `.<resolvedValue>` so downstream resolvers see a
 * flat dotted path.
 *
 * Example (pricing-tier-v1.md §6): `pricing[__bind.tier.id].name` with `__bind.tier.id → "basic"`
 * becomes `pricing.basic.name`. Unresolved bodies return `undefined` — the caller treats that
 * as "path can't be constructed, lookup misses" and the surrounding `?? fallback` takes over.
 */
function expandNamespacePath(
  path: string,
  resolvers: Resolvers,
  warnings: string[],
): string | undefined {
  let i = 0
  let out = ""
  while (i < path.length) {
    const ch = path[i]
    if (ch !== "[") {
      out += ch
      i++
      continue
    }
    const close = path.indexOf("]", i)
    if (close === -1) {
      warnings.push(`unterminated bracket in namespace path: ${path}`)
      return undefined
    }
    const body = path.slice(i + 1, close).trim()
    // Literal integer / quoted string — preserve the bracket so existing resolvers (e.g.
    // bind-resolver splitting on `.[]`) keep working unchanged.
    if (/^\d+$/.test(body)) {
      out += `[${body}]`
      i = close + 1
      continue
    }
    if (
      (body.startsWith('"') && body.endsWith('"')) ||
      (body.startsWith("'") && body.endsWith("'"))
    ) {
      out += `[${body}]`
      i = close + 1
      continue
    }
    // Dynamic body — evaluate recursively. Undefined / null → can't form a valid path.
    const value = evalOne(body, resolvers, warnings)
    if (value === undefined || value === null) return undefined
    out += `.${String(value)}`
    i = close + 1
  }
  return out
}

/**
 * Apply a pipe expression (e.g., `format:currency`, `map:status-variant`, `truncate:20`) to a
 * value using the supplied registry. Unknown pipes / arguments emit warnings and return the
 * original value when possible.
 */
function applyPipe(
  v: unknown,
  pipeExpr: string,
  registry: PipeRegistry,
  warnings: string[],
): unknown {
  const colonIdx = pipeExpr.indexOf(":")
  const name = colonIdx === -1 ? pipeExpr : pipeExpr.slice(0, colonIdx).trim()
  const arg = colonIdx === -1 ? undefined : pipeExpr.slice(colonIdx + 1).trim()

  switch (name) {
    case "format": {
      if (arg === undefined) {
        warnings.push("unknown format pipe arg: (missing)")
        return typeof v === "string" ? v : String(v)
      }
      // `format:truncate:20` → pipe name "truncate", arg "20".
      const nestedColon = arg.indexOf(":")
      const fmtName = nestedColon === -1 ? arg : arg.slice(0, nestedColon).trim()
      const fmtArg = nestedColon === -1 ? undefined : arg.slice(nestedColon + 1).trim()
      const fn = registry.format[fmtName]
      if (!fn) {
        warnings.push(`unknown format pipe arg: ${fmtName}`)
        return typeof v === "string" ? v : String(v)
      }
      return fn(v, fmtArg)
    }
    case "map": {
      if (arg === undefined) {
        warnings.push("unknown map pipe arg: (missing)")
        return v
      }
      const table = registry.map[arg]
      if (!table) {
        warnings.push(`unknown map pipe arg: ${arg}`)
        return v
      }
      const key = typeof v === "string" ? v : String(v ?? "")
      if (!(key in table)) {
        warnings.push(`map:${arg} has no entry for key: ${key}`)
        return v
      }
      return table[key]
    }
    default:
      warnings.push(`unknown pipe: ${name}`)
      return v
  }
}

// ---------------------------------------------------------------------------------------------
// Parser helpers
// ---------------------------------------------------------------------------------------------

/**
 * Find the first occurrence of a top-level operator token in `s`, skipping any characters inside
 * balanced parentheses, brackets, or quoted strings. Returns -1 if not found.
 */
function findTopLevelOp(s: string, op: string): number {
  let depth = 0
  let i = 0
  while (i < s.length) {
    const c = s[i]
    if (c === "'" || c === '"') {
      // skip quoted string
      const quote = c
      i++
      while (i < s.length && s[i] !== quote) {
        if (s[i] === "\\") i++
        i++
      }
      i++
      continue
    }
    if (c === "(" || c === "[") {
      depth++
      i++
      continue
    }
    if (c === ")" || c === "]") {
      depth--
      i++
      continue
    }
    if (depth === 0 && s.startsWith(op, i)) {
      // Disambiguate overlapping tokens: `===` must not be caught by `==`, `!==` not by `!=`,
      // `>=/<=` not by `>/<`, `||` not by `|`, `&&` vs `&` (n/a), `??` stands alone.
      const prev = i > 0 ? s[i - 1] : ""
      const next = i + op.length < s.length ? s[i + op.length] : ""
      // For `==` and `!=` make sure we aren't looking at `===` / `!==`.
      if ((op === "==" || op === "!=") && next === "=") {
        i++
        continue
      }
      // For `!` we aren't a scan target — but for `|` guard against `||`.
      if (op === "|" && (prev === "|" || next === "|")) {
        i++
        continue
      }
      // For `<` / `>` guard against `<=` / `>=` (longer token wins the match).
      if ((op === "<" || op === ">") && next === "=") {
        i++
        continue
      }
      return i
    }
    i++
  }
  return -1
}

/**
 * Given an expression, split it at the top-level ternary `?` / `:` pair. Returns null if there
 * is no ternary at this level. The `?` must not be followed immediately by another `?` (to avoid
 * catching hypothetical optional-chaining tokens, though we don't use them).
 */
function splitTernary(
  s: string,
): { cond: string; then: string; else: string } | null {
  let depth = 0
  let i = 0
  let qIdx = -1
  while (i < s.length) {
    const c = s[i]
    if (c === "'" || c === '"') {
      const quote = c
      i++
      while (i < s.length && s[i] !== quote) {
        if (s[i] === "\\") i++
        i++
      }
      i++
      continue
    }
    if (c === "(" || c === "[") {
      depth++
      i++
      continue
    }
    if (c === ")" || c === "]") {
      depth--
      i++
      continue
    }
    if (depth === 0 && c === "?") {
      // Not a `??` nullish operator.
      if (s[i + 1] === "?") {
        i += 2
        continue
      }
      qIdx = i
      break
    }
    i++
  }
  if (qIdx === -1) return null

  // Find matching `:` for this `?` at the same depth.
  let depth2 = 0
  let j = qIdx + 1
  while (j < s.length) {
    const c = s[j]
    if (c === "'" || c === '"') {
      const quote = c
      j++
      while (j < s.length && s[j] !== quote) {
        if (s[j] === "\\") j++
        j++
      }
      j++
      continue
    }
    if (c === "(" || c === "[") {
      depth2++
      j++
      continue
    }
    if (c === ")" || c === "]") {
      depth2--
      j++
      continue
    }
    if (c === "?" && s[j + 1] !== "?") {
      // nested ternary — skip to its `:`.
      depth2++
      j++
      continue
    }
    if (depth2 === 0 && c === ":") {
      return {
        cond: s.slice(0, qIdx).trim(),
        then: s.slice(qIdx + 1, j).trim(),
        else: s.slice(j + 1).trim(),
      }
    }
    if (c === ":" && depth2 > 0) {
      depth2--
      j++
      continue
    }
    j++
  }
  return null
}

/** Find matching `)` for `(` at `start`. Returns index of matching paren or -1. */
function matchingParen(s: string, start: number): number {
  let depth = 0
  for (let i = start; i < s.length; i++) {
    if (s[i] === "(") depth++
    else if (s[i] === ")") {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

/** JS-style truthiness: null/undefined/false/0/"" are falsy, everything else truthy. */
function truthy(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (v === false) return false
  if (typeof v === "number" && (v === 0 || Number.isNaN(v))) return false
  if (typeof v === "string" && v === "") return false
  return true
}

/** Coerce to number for relational comparisons. Dates → epoch ms, strings → Number(). */
function toNumber(v: unknown): number {
  if (typeof v === "number") return v
  if (v instanceof Date) return v.getTime()
  if (v === null || v === undefined) return NaN
  return Number(v as never)
}
